import React, { useState, useEffect } from 'react';
import {
  Box, 
  Tooltip,
  Button,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Search, SearchOff, UploadFile, AccountBox } from '@mui/icons-material';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import TableComponent from '../components/TableComponent';
import API from '../services/api';
import downloadFile from '../services/download';
import dateFns, { format } from 'date-fns';

const QRPassesPage = () => {

  const navigate = useNavigate();

  const emptySearchValues = {
    startDate: null,
    endDate: null,
    sortBy: null,
    activeOnly: false,
    createdInPeriod: false
  }

  const [searchValues, setSearchValues] = useState({
      ...emptySearchValues
    });
    
    const [searchValuesPrepared, setSearchValuesPrepared] = useState({
      ...emptySearchValues
    });
  
    const [forceUpdateCounter, setForceUpdateCounter] = useState(0);
    const [resetPage, setResetPage] = useState(false);

  const columns = [
    { id: 'active', label: 'Active', type: 'boolean', width: '5%' },
    { id: 'startDate', label: 'Start Date', type: 'date', width: '12%' },
    { id: 'endDate', label: 'End Date', type: 'date', width: '12%' },
    { id: 'daysLeft', label: 'Days Left', width: '12%' },
    { id: 'amount', label: 'Amount', type: 'currency', width: '5%'},
    { id: 'customer.name', label: 'Name', width: '17%', action: {
        handler: (row) => handleProfileClick(row)
    }},
    { id: 'customer.phone', label: 'Phone', width: '16%' },
    { id: 'customer.email', label: 'Email', width: '16%' },
    { id: 'actions', label: '', width: '5%' },
  ];

  const handleProfileClick = (row) => {
    navigate(`/customers/${row.customer.id}/profile`, {
      state: { from: location.pathname }
    });
  };

  const actionsHandlers = {
      actions: [
        {
          name: 'profile',
          label: <AccountBox/>,
          tooltip: 'Customer Profile',
          handler: (row) => handleProfileClick(row)
        }
      ]
    };
  
  const sortOptions = [
    { value: 'startDate', label: 'Start Date' },
    { value: 'endDate', label: 'End Date' },
  ];

  const addSearchParamsToURL = (params) => {
    if (searchValues.startDate) {
      params.append('startDate', format(searchValues.startDate, 'yyyy-MM-dd'));
    }
    if (searchValues.endDate) {
      params.append('endDate', format(searchValues.endDate, 'yyyy-MM-dd'));
    }
    if (searchValues.sortBy) {
      params.append('sortBy', '-' + searchValues.sortBy.value);
    }
    if (searchValues.activeOnly) {
      params.append('activeOnly', searchValues.activeOnly);
    }
    if (searchValues.createdInPeriod) {
      params.append('createdInPeriod', searchValues.createdInPeriod);
    }
  }

  const getData = async (page, limit) => {
    // PassLogs loading
    const params = new URLSearchParams();
    addSearchParamsToURL(params);
    params.append('page', page);
    params.append('limit', limit);
    const response = await API.get(`/api/passes?${params.toString()}`);

    // Data processing
    const processedData = {
      ...response.data,
      rows: response.data.qrCodes
    };

    delete processedData.passLogs;

    return processedData;
  };

  const updateData = () => {
    setForceUpdateCounter(prev => prev + 1);
  }

  const handleSearch = () => {
    // If search values are not changed and user just clicked button search
    const isChanged = Object.keys(searchValuesPrepared).some(key =>
      searchValues[key] !== searchValuesPrepared[key]
      );
    if (!isChanged) return;

    setSearchValues({
        ...searchValuesPrepared
    });
    setResetPage(true);
    updateData();
  }

  const handleSearchChange = (field, value) => {
    setSearchValuesPrepared({
        ...searchValuesPrepared,
        [field]: value
    });
  };

  const resetSearch = () => {
    // Always clean text in search fields
    setSearchValuesPrepared({...emptySearchValues});

    // If search was not run before and current search values are empty
    const isChanged = Object.keys(emptySearchValues).some(key =>
      searchValues[key] !== emptySearchValues[key]
      );
    if (!isChanged) return;

    setSearchValues({...emptySearchValues});
    setResetPage(true);
    updateData();
  };

  const handleExportDataClick = () => {
    const params = new URLSearchParams();
    addSearchParamsToURL(params);
    downloadFile(`/api/passes/exportData?${params.toString()}`, 'QRPasses.xlsx');
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '20px',
        margin: 'auto',
        width: '80%'
      }}
    >
      <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            margin: 'auto',
            gap: '10px',
            marginBottom: '20px',
        }}
        >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
                label="Start Date"
                value={searchValuesPrepared.startDate}
                format="dd.MM.yyyy"
                slotProps={{ textField: { size: 'small' } }}
                onChange={(value) => handleSearchChange('startDate', value)}
                fullWidth
                margin="normal"
            />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
                label="End Date"
                value={searchValuesPrepared.endDate}
                format="dd.MM.yyyy"
                slotProps={{ textField: { size: 'small' } }}
                onChange={(value) => handleSearchChange('endDate', value)}
                fullWidth
                margin="normal"
            />
        </LocalizationProvider>
        <Autocomplete
          value={searchValuesPrepared.sortBy}
          size= 'small'
          sx={{
              width: '170px'
          }}
          onChange={(event, newValue) => {
            handleSearchChange('sortBy', newValue);
          }}
          options={sortOptions}
          getOptionValue={(option) => option.value}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              label="SortBy"
              fullWidth
            />
          )} />
        <Tooltip title="Run Search">
            <Button
                variant="outlined"
                onClick={handleSearch}
                sx={{
                  height: '40px',
                }}
            >
                <Search />
            </Button>
        </Tooltip>
        <Tooltip title="Reset Search">
            <Button
                variant="outlined"
                onClick={resetSearch}
                sx={{
                  height: '40px',
                }}
            >
                <SearchOff />
            </Button>
        </Tooltip>
        <Tooltip title="Export Data">
            <Button 
                variant="contained" 
                onClick={handleExportDataClick}
                sx={{
                  height: '40px'
                }}
            >
                <UploadFile />
            </Button>
        </Tooltip>
      </Box>

      <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            margin: 'auto',
            gap: '10px',
            marginBottom: '20px',
        }}
        >
        <FormControlLabel
          control={
              <Checkbox
                  checked={searchValuesPrepared.activeOnly}
                  onChange={(e) => handleSearchChange('activeOnly', e.target.checked)}
                  name="active"
              />
          }
          label="Active Only"
          />
          <FormControlLabel
          control={
              <Checkbox
                  checked={searchValuesPrepared.createdInPeriod}
                  onChange={(e) => handleSearchChange('createdInPeriod', e.target.checked)}
                  name="active"
              />
          }
          label="Created during the period"
          />
      </Box>

      <TableComponent
        columns={columns}
        getData={getData}
        height='60vh'
        actionsHandlers={actionsHandlers}
        forceUpdate={forceUpdateCounter}
        resetPage={resetPage}
        onDataLoaded={() => setResetPage(false)}
      />

    </Box>
  );
};

export default QRPassesPage;
